import type { SpeciesData } from '../types';
import { speciesColor } from '../palette';

const NS = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export class UmapScatter {
  private readonly svg: SVGSVGElement;
  private groups: SVGGElement[] = [];
  private selectedIdx = -1;
  private readonly colorFn: (sp: SpeciesData, i: number) => string;

  constructor(
    container: HTMLElement,
    private readonly species: SpeciesData[],
    private readonly onSelect: (index: number) => void,
    colorFn?: (sp: SpeciesData, i: number) => string,
    width = 260,
    height = 270,
  ) {
    this.colorFn = colorFn ?? ((sp, i) => speciesColor(sp.dom_area, i));
    this.svg = document.createElementNS(NS, 'svg');
    this.svg.id = 'sm-umap-svg';
    this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    this.svg.setAttribute('width', String(width));
    this.svg.setAttribute('height', String(height));
    container.appendChild(this.svg);

    this.build(width, height);
  }

  private build(W: number, H: number): void {
    const pad = 22;
    const xs = this.species.map(d => d.ux);
    const ys = this.species.map(d => d.uy);
    const x0 = Math.min(...xs), x1 = Math.max(...xs);
    const y0 = Math.min(...ys), y1 = Math.max(...ys);
    const sc = Math.min((W - pad * 2) / (x1 - x0), (H - pad * 2) / (y1 - y0));
    const ox = pad + (W - pad * 2 - (x1 - x0) * sc) / 2;
    const oy = pad + (H - pad * 2 - (y1 - y0) * sc) / 2;

    const px = (d: SpeciesData): number => ox + (d.ux - x0) * sc;
    const py = (d: SpeciesData): number => oy + (d.uy - y0) * sc;
    const maxC = Math.max(...this.species.map(d => d.count));

    // SVG defs: glow filters
    const defs = document.createElementNS(NS, 'defs');
    defs.innerHTML = `
      <filter id="sm-g1" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="2" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
      <filter id="sm-g2" x="-80%" y="-80%" width="260%" height="260%">
        <feGaussianBlur stdDeviation="4" result="b"/>
        <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>`;
    this.svg.appendChild(defs);

    // Grid
    const grid = document.createElementNS(NS, 'g');
    for (let x = 0; x < W; x += 40) {
      grid.appendChild(svgEl('line', { x1: String(x), y1: '0', x2: String(x), y2: String(H), stroke: 'rgba(151,184,203,.04)', 'stroke-width': '1' }));
    }
    for (let y = 0; y < H; y += 40) {
      grid.appendChild(svgEl('line', { x1: '0', y1: String(y), x2: String(W), y2: String(y), stroke: 'rgba(151,184,203,.04)', 'stroke-width': '1' }));
    }
    this.svg.appendChild(grid);

    // Points
    this.groups = [];
    this.species.forEach((d, i) => {
      const col = this.colorFn(d, i);
      const r   = 3 + Math.sqrt(d.count / maxC) * 8;
      const g   = document.createElementNS(NS, 'g');
      g.style.cursor = 'pointer';
      g.dataset['i'] = String(i);

      const circle = svgEl('circle', {
        cx: String(px(d)), cy: String(py(d)), r: String(r),
        fill: col, opacity: '0.75', filter: 'url(#sm-g1)',
      });
      g.appendChild(circle);

      // Label top-5 by count
      if (i < 5) {
        const lbl = document.createElementNS(NS, 'text');
        lbl.setAttribute('x',           String(px(d) + r + 3));
        lbl.setAttribute('y',           String(py(d) + 4));
        lbl.setAttribute('fill',        col);
        lbl.setAttribute('font-size',   '8');
        lbl.setAttribute('font-family', 'Courier New,monospace');
        lbl.setAttribute('font-style',  'italic');
        lbl.setAttribute('filter',      'url(#sm-g1)');
        lbl.textContent = d.name.length > 18 ? d.name.slice(0, 16) + '…' : d.name;
        g.appendChild(lbl);
      }

      g.addEventListener('click',       () => this.select(i));
      g.addEventListener('mouseenter',  () => { circle.setAttribute('opacity', '1'); circle.setAttribute('filter', 'url(#sm-g2)'); });
      g.addEventListener('mouseleave',  () => { if (i !== this.selectedIdx) { circle.setAttribute('opacity', '0.75'); circle.setAttribute('filter', 'url(#sm-g1)'); } });

      this.svg.appendChild(g);
      this.groups.push(g);
    });
  }

  select(i: number): void {
    this.selectedIdx = i;
    const maxC = Math.max(...this.species.map(d => d.count));

    this.groups.forEach((g, idx) => {
      const circle = g.querySelector('circle')!;
      const d      = this.species[idx];
      const r      = 3 + Math.sqrt(d.count / maxC) * 8;

      if (idx === i) {
        circle.setAttribute('opacity', '1');
        circle.setAttribute('r',      String(r + 3));
        circle.setAttribute('filter', 'url(#sm-g2)');
      } else {
        circle.setAttribute('opacity', '0.45');
        circle.setAttribute('r',      String(r));
        circle.setAttribute('filter', 'url(#sm-g1)');
      }
    });

    this.onSelect(i);
  }
}
